import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    GraduationCap, Books, CheckCircle, Clock, Lock, Play, ArrowRight,
    Certificate, Trophy, Stethoscope, Tooth, ArrowLeft, Warning,
    CaretRight, CaretDown, CaretUp, DownloadSimple, ChartLineUp, Sparkle,
    Bell, Question, Plus, Lightning, ListChecks, PlayCircle,
    Star, BookOpen, ClipboardText, Users as UsersIcon, Exam, SealCheck,
    Desktop, Briefcase, Circle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/*  ICPP Course Detail — Level 2 of the Invisique Learning Academy
    ────────────────────────────────────────────────────────────────
    Rendered at /doctor/academy/icpp. Uses a course-scoped left
    sidebar + custom topbar (Coursera-style) INSTEAD of the shared
    PortalLayout — the learning experience gets its own dedicated
    shell so the doctor can focus. Route is registered OUTSIDE the
    /doctor PortalLayout wrapper in App.js.

    Native teal (#007C9D) + rose accent palette throughout — the
    mockup's blue/emerald palette was mapped to the app's existing
    identity so the Academy feels part of the portal.
*/


/* ─── Track & phase metadata ──────────────────────────────────── */

const TRACK_META = {
    gp: {
        label: "GP Track",
        subtitle: "General Practitioner",
        short: "GP",
        icon: Tooth,
        pillIcon: Tooth,
        pitch: "Designed for general dentists integrating clear aligner therapy into their practice for the first time.",
        features: [
            "Case selection fundamentals",
            "Basic to intermediate cases",
            "Patient consultation scripts",
            "Practice setup & workflow",
            "20 modules · ~20 hrs",
        ],
        iconTileCls: "bg-primary/10 text-primary border border-primary/20",
        subtitleCls: "text-primary",
        checkCls: "bg-primary/10 text-primary",
        buttonCls: "bg-primary hover:bg-primary/90 text-primary-foreground",
        borderHoverCls: "hover:border-primary/40",
        chip: "bg-primary/10 text-primary border border-primary/20",
        text: "text-primary",
        button: "bg-primary hover:bg-primary/90 text-primary-foreground",
    },
    ortho: {
        label: "Orthodontist Track",
        subtitle: "Orthodontist",
        short: "Ortho",
        icon: GraduationCap,
        pillIcon: GraduationCap,
        pitch: "Built for orthodontists seeking advanced aligner techniques, complex case management, and full practice integration.",
        features: [
            "Complex case management",
            "Advanced biomechanics",
            "Interproximal reduction techniques",
            "Full arch & surgical cases",
            "24 modules · ~28 hrs",
        ],
        iconTileCls: "bg-teal-50 text-teal-700 border border-teal-200",
        subtitleCls: "text-teal-700",
        checkCls: "bg-teal-50 text-teal-700",
        buttonCls: "bg-teal-600 hover:bg-teal-700 text-white",
        borderHoverCls: "hover:border-teal-400",
        chip: "bg-teal-50 text-teal-700 border border-teal-200",
        text: "text-teal-700",
        button: "bg-teal-600 hover:bg-teal-700 text-white",
    },
};

const PHASE_META_BY_ID = {
    phase_1: { icon: Books,        subtitle: "Foundation" },
    phase_2: { icon: Stethoscope,  subtitle: "Clinical Execution" },
    phase_3: { icon: ChartLineUp,  subtitle: "Practice Growth" },
    phase_4: { icon: Trophy,       subtitle: "Mastery" },
};

/* Deterministic icon rotation for module cards — indexed by module index */
const MODULE_ICONS = [BookOpen, ClipboardText, UsersIcon, Desktop, SealCheck, Briefcase, Exam];


/* ─── Helpers ─────────────────────────────────────────────────── */

function getInitials(name) {
    if (!name || typeof name !== "string") return "DR";
    const clean = name.replace(/^Dr\.?\s+/i, "").trim();
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "DR";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function moduleIsComplete(m, progress) {
    const passed = !!progress?.quiz_scores?.[m.id]?.passed;
    const viewed = progress?.viewed_modules?.includes(m.id);
    return passed && viewed;
}

function totalDurationMinutes(phases, track) {
    return phases.reduce((phaseAcc, phase) => {
        const mods = phase.modules || [];
        return phaseAcc + mods.reduce((acc, m) => acc + (m.durations?.[track] || 0), 0);
    }, 0);
}

function totalModules(phases) {
    return phases.reduce((acc, p) => acc + (p.modules?.length || 0), 0);
}


/* ─── Main component ──────────────────────────────────────────── */

export default function LearningAcademy() {
    const [content, setContent] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectingTrack, setSelectingTrack] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, p] = await Promise.all([
                api.get("/academy/content"),
                api.get("/academy/progress"),
            ]);
            setContent(c.data);
            setProgress(p.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load the Academy — please refresh.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doSelectTrack = async (track) => {
        try {
            await api.post("/academy/track/select", { track });
            const p = await api.get("/academy/progress");
            setProgress(p.data);
            setSelectingTrack(null);
            toast.success(`Track locked — ${TRACK_META[track].label}. Let's begin.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't set track.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 p-8">
                <div className="max-w-5xl mx-auto space-y-6">
                    <div className="h-14 bg-white rounded-xl border border-slate-200 animate-pulse" />
                    <div className="h-72 bg-slate-900/95 rounded-2xl animate-pulse" />
                    <div className="h-24 bg-white rounded-xl border border-slate-200 animate-pulse" />
                </div>
            </div>
        );
    }

    // Track not selected — one-shot picker
    if (!progress?.track) {
        return (
            <>
                <TrackSelectionScreen onPick={(t) => setSelectingTrack(t)} />
                <ConfirmTrackDialog
                    track={selectingTrack}
                    onConfirm={() => selectingTrack && doSelectTrack(selectingTrack)}
                    onCancel={() => setSelectingTrack(null)}
                />
            </>
        );
    }

    const track = progress.track;
    const phases = content?.phases || [];
    const phasesCompleted = progress.phases_completed || [];

    // Currently active phase = first active + has-modules + not-completed
    const activePhase = phases.find(
        (p) => p.state === "active" && (p.modules?.length || 0) > 0 && !phasesCompleted.includes(p.id),
    );

    const openModule = (moduleId) => navigate(`/academy/module/${moduleId}`);

    return (
        <div className="min-h-screen flex flex-col bg-slate-50" data-testid="academy-curriculum">
            <AcademyTopbar
                user={user}
                track={track}
                activePhaseLabel={activePhase?.label}
            />
            <div className="flex flex-1 overflow-hidden">
                <AcademyCourseSidebar
                    phases={phases}
                    progress={progress}
                    track={track}
                    activePhaseId={activePhase?.id}
                    onOpenModule={openModule}
                />
                <main className="flex-1 overflow-y-auto bg-slate-50">
                    <CourseHero
                        track={track}
                        phases={phases}
                        progress={progress}
                        activePhase={activePhase}
                        onOpenModule={openModule}
                    />
                    <ProgressStrip
                        progress={progress}
                        phases={phases}
                        activePhaseId={activePhase?.id}
                    />
                    <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-10 pb-20">
                        {/* Certification Journey (phase stepper) */}
                        <SectionHeading
                            title="Certification Journey"
                            sub="Complete each phase in order to unlock the next. Phases are sequential and gated."
                        />
                        <PhaseStepper
                            phases={phases}
                            progress={progress}
                            activePhaseId={activePhase?.id}
                        />

                        {/* Active Phase — card + module accordion */}
                        {activePhase && (
                            <>
                                <SectionHeading
                                    className="mt-10"
                                    title={`Phase ${activePhase.index} — ${activePhase.label}`}
                                    sub={activePhase.description || "Self-paced modules — complete them in order to unlock the next phase."}
                                />
                                <ActivePhaseCard phase={activePhase} progress={progress} track={track} />
                                <ModuleAccordion
                                    modules={activePhase.modules || []}
                                    phase={activePhase}
                                    track={track}
                                    progress={progress}
                                    onOpen={openModule}
                                />
                            </>
                        )}
                        {!activePhase && (
                            <NoActivePhaseNotice phasesCompleted={phasesCompleted.length} />
                        )}

                        {/* Certificate download banner (celebration) */}
                        <CertificateBanner
                            progress={progress}
                            content={content}
                        />

                        {/* Locked/upcoming phase strips */}
                        <LockedPhaseBlocks
                            phases={phases}
                            activePhaseId={activePhase?.id}
                            phasesCompleted={phasesCompleted}
                            progress={progress}
                        />
                    </div>
                </main>
            </div>
        </div>
    );
}


/* ─── AcademyTopbar ────────────────────────────────────────────── */

function AcademyTopbar({ user, track, activePhaseLabel }) {
    const displayName = user?.doctor_name || user?.name || "Provider";
    const initials = getInitials(displayName);
    const trackLabel = TRACK_META[track]?.label || "GP Track";
    return (
        <header
            className="h-14 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 md:px-6"
            data-testid="academy-topbar"
        >
            <div className="flex items-center gap-4 md:gap-5 min-w-0">
                <Link
                    to="/doctor/dashboard"
                    className="flex items-center gap-2 no-underline shrink-0"
                    data-testid="academy-topbar-home"
                >
                    <div className="w-8 h-8 rounded-lg bg-teal-600 grid place-items-center shadow-sm">
                        <Plus size={16} weight="bold" className="text-white" />
                    </div>
                    <span className="font-display font-bold text-[15px] text-slate-900 tracking-tight hidden sm:inline">
                        Invisique<span className="text-teal-600">™</span>
                    </span>
                </Link>
                <div className="hidden md:block w-px h-5 bg-slate-200" />
                <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500 font-medium min-w-0">
                    <Link
                        to="/doctor/academy"
                        className="hover:text-teal-700 transition-colors flex items-center gap-1 no-underline shrink-0"
                        data-testid="academy-topbar-catalogue"
                    >
                        <GraduationCap size={13} weight="regular" />
                        <span className="hidden md:inline">Academy</span>
                    </Link>
                    <CaretRight size={9} weight="bold" className="text-slate-400" />
                    <span className="text-slate-700">ICPP</span>
                    <CaretRight size={9} weight="bold" className="text-slate-400" />
                    <span className="text-slate-900 font-semibold truncate">{activePhaseLabel || trackLabel}</span>
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors"
                    data-testid="academy-topbar-notifications"
                    aria-label="Notifications"
                >
                    <Bell size={15} weight="regular" />
                </button>
                <button
                    type="button"
                    className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors"
                    data-testid="academy-topbar-help"
                    aria-label="Help"
                >
                    <Question size={15} weight="regular" />
                </button>
                <Link
                    to="/doctor/profile"
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white text-[11px] font-bold flex items-center justify-center no-underline shadow-sm hover:shadow"
                    data-testid="academy-topbar-avatar"
                    title={displayName}
                >
                    {initials}
                </Link>
            </div>
        </header>
    );
}


/* ─── AcademyCourseSidebar ────────────────────────────────────── */

function AcademyCourseSidebar({ phases, progress, track, activePhaseId, onOpenModule }) {
    const [openPhaseId, setOpenPhaseId] = useState(activePhaseId);
    useEffect(() => {
        if (activePhaseId) setOpenPhaseId(activePhaseId);
    }, [activePhaseId]);

    const phasesCompleted = new Set(progress?.phases_completed || []);
    const totalPhases = phases.length || 4;

    // Overall progress = weighted across phases
    const activeRollup = activePhaseId ? progress?.phase_progress?.[activePhaseId] : null;
    const partial = activeRollup && !activeRollup.completed ? activeRollup.percent / 100 : 0;
    const overallPct = Math.round(
        Math.min(100, ((phasesCompleted.size + partial) / totalPhases) * 100),
    );

    const trackMeta = TRACK_META[track];

    return (
        <nav
            className="w-72 shrink-0 bg-white border-r border-slate-200 overflow-y-auto hidden lg:flex lg:flex-col"
            data-testid="academy-course-sidebar"
        >
            {/* Sidebar header */}
            <div className="px-5 pt-6 pb-4 border-b border-slate-100">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">
                    ICPP · {trackMeta?.short || "GP"} Track
                </div>
                <div className="text-[13px] font-bold text-slate-900 leading-snug tracking-tight">
                    Invisique™ Certified Provider Program
                </div>
                <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] font-medium text-slate-500 mb-1.5">
                        <span>Overall progress</span>
                        <span className="font-bold text-teal-700 tabular-nums">{overallPct}%</span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-700"
                            style={{ width: `${overallPct}%` }}
                            data-testid="sidebar-overall-progress"
                        />
                    </div>
                </div>
            </div>

            {/* Phases (accordion) */}
            <div className="px-3 py-3 flex-1">
                {phases.map((phase) => (
                    <SidebarPhaseGroup
                        key={phase.id}
                        phase={phase}
                        isOpen={openPhaseId === phase.id}
                        onToggle={() => setOpenPhaseId((prev) => (prev === phase.id ? null : phase.id))}
                        isActive={phase.id === activePhaseId}
                        isCompleted={phasesCompleted.has(phase.id)}
                        progress={progress}
                        onOpenModule={onOpenModule}
                    />
                ))}
            </div>
        </nav>
    );
}


function SidebarPhaseGroup({ phase, isOpen, onToggle, isActive, isCompleted, progress, onOpenModule }) {
    const modules = phase.modules || [];
    const isLocked = !isActive && !isCompleted;

    // Find first not-complete module inside this phase — "current" indicator
    const currentIdx = modules.findIndex((m) => !moduleIsComplete(m, progress));
    const currentModuleId = currentIdx >= 0 && isActive ? modules[currentIdx].id : null;

    let dotCls, dotContent;
    if (isCompleted) {
        dotCls = "bg-emerald-500 text-white";
        dotContent = <CheckCircle size={12} weight="fill" />;
    } else if (isActive) {
        dotCls = "bg-teal-600 text-white";
        dotContent = <span className="text-[10px] font-bold tabular-nums leading-none">{phase.index}</span>;
    } else {
        dotCls = "bg-slate-100 text-slate-400 border border-slate-200";
        dotContent = <span className="text-[10px] font-bold tabular-nums leading-none">{phase.index}</span>;
    }
    const titleCls = isLocked ? "text-slate-400" : "text-slate-700";

    return (
        <div className={`mb-1 ${isOpen ? "" : ""}`} data-testid={`sidebar-phase-group-${phase.id}`}>
            <button
                type="button"
                onClick={onToggle}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors ${
                    isOpen ? "bg-slate-100" : "hover:bg-slate-50"
                }`}
                data-testid={`sidebar-phase-toggle-${phase.id}`}
                aria-expanded={isOpen}
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${dotCls}`}>
                        {dotContent}
                    </div>
                    <span className={`text-[12.5px] font-semibold truncate ${titleCls}`}>
                        {phase.label}
                    </span>
                </div>
                {isOpen ? (
                    <CaretUp size={11} weight="bold" className="text-slate-400 shrink-0" />
                ) : (
                    <CaretDown size={11} weight="bold" className="text-slate-400 shrink-0" />
                )}
            </button>
            {isOpen && (
                <div className="pl-5 mt-0.5 mb-1 space-y-0.5">
                    {modules.length === 0 && (
                        <div className="text-[11.5px] text-slate-400 italic px-2.5 py-1.5">
                            {isLocked ? "Locked" : "Coming soon"}
                        </div>
                    )}
                    {modules.map((m, idx) => (
                        <SidebarModuleItem
                            key={m.id}
                            module={m}
                            isCurrent={m.id === currentModuleId}
                            isCompleted={moduleIsComplete(m, progress)}
                            isPhaseLocked={isLocked}
                            priorAllComplete={modules.slice(0, idx).every((pm) => moduleIsComplete(pm, progress))}
                            onOpen={() => onOpenModule(m.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function SidebarModuleItem({ module: m, isCurrent, isCompleted, isPhaseLocked, priorAllComplete, onOpen }) {
    const isLocked = isPhaseLocked || (!isCompleted && !isCurrent && !priorAllComplete);

    let checkCls, checkIcon;
    if (isCompleted) {
        checkCls = "bg-emerald-500 text-white";
        checkIcon = <CheckCircle size={10} weight="fill" />;
    } else if (isCurrent) {
        checkCls = "bg-teal-500 text-white";
        checkIcon = <Play size={9} weight="fill" />;
    } else {
        checkCls = "bg-transparent border border-slate-200 text-slate-400";
        checkIcon = isLocked ? <Lock size={9} weight="regular" /> : <span className="text-[9px] font-bold tabular-nums leading-none">{m.index}</span>;
    }

    let textCls;
    if (isCurrent) textCls = "text-teal-700 font-semibold";
    else if (isLocked) textCls = "text-slate-400";
    else textCls = "text-slate-600";

    const rowCls = `flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors ${
        isLocked ? "cursor-default" : "cursor-pointer hover:bg-slate-50"
    } ${isCurrent ? "bg-teal-50/70" : ""}`;

    const inner = (
        <>
            <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${checkCls}`}>
                {checkIcon}
            </div>
            <span className={`text-[11.5px] leading-snug ${textCls} truncate`}>{m.title}</span>
        </>
    );

    if (isLocked) {
        return (
            <div className={rowCls} data-testid={`sidebar-module-${m.id}`} data-locked="true">
                {inner}
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={onOpen}
            className={`w-full text-left ${rowCls}`}
            data-testid={`sidebar-module-${m.id}`}
        >
            {inner}
        </button>
    );
}


/* ─── CourseHero — dark bg with radial glow ────────────────────── */

function CourseHero({ track, phases, progress, activePhase, onOpenModule }) {
    const trackMeta = TRACK_META[track];
    const phasesWithModules = phases.filter((p) => (p.modules?.length || 0) > 0);
    const totalMods = totalModules(phasesWithModules);
    const totalMins = totalDurationMinutes(phasesWithModules, track);
    const totalHrs = totalMins > 0 ? `~${Math.round(totalMins / 60)}h` : "—";

    // Count completed modules across all phases
    let doneMods = 0;
    phases.forEach((p) => {
        (p.modules || []).forEach((m) => {
            if (moduleIsComplete(m, progress)) doneMods += 1;
        });
    });

    const phasesCompleted = new Set(progress?.phases_completed || []);
    const activeRollup = activePhase ? progress?.phase_progress?.[activePhase.id] : null;
    const partial = activeRollup && !activeRollup.completed ? activeRollup.percent / 100 : 0;
    const totalPhases = phases.length || 4;
    const overallPct = Math.round(
        Math.min(100, ((phasesCompleted.size + partial) / totalPhases) * 100),
    );

    const remainingMins = phasesWithModules.reduce((acc, phase) => {
        return acc + (phase.modules || []).reduce((macc, m) => {
            if (moduleIsComplete(m, progress)) return macc;
            return macc + (m.durations?.[track] || 0);
        }, 0);
    }, 0);
    const remainingHrs = remainingMins > 0 ? `~${Math.round(remainingMins / 60)}h` : "0h";

    // Current module for the Resume CTA
    const currentModule = activePhase
        ? (activePhase.modules || []).find((m) => !moduleIsComplete(m, progress))
        : null;

    return (
        <div
            className="relative overflow-hidden bg-slate-900 text-white px-6 md:px-12 pt-10 pb-8"
            data-testid="course-hero"
        >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top_right,rgba(0,124,157,0.32),transparent_55%),radial-gradient(ellipse_at_bottom_left,hsla(340,75%,45%,0.14),transparent_55%)]" />
            <div className="relative">
                <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-teal-300 mb-3">
                    <GraduationCap size={12} weight="fill" />
                    Invisique™ Certified Provider Program
                </div>
                <h1
                    className="font-display text-[26px] md:text-[32px] font-bold text-white tracking-tight leading-[1.15] max-w-2xl mb-3"
                    data-testid="academy-title"
                >
                    ICPP — {trackMeta?.subtitle || "General Practitioner"} Track
                </h1>
                <p className="text-[13.5px] text-slate-300 leading-relaxed max-w-xl mb-5">
                    Master the full spectrum of clear aligner therapy — from patient selection and treatment planning to clinical execution and case validation. Earn your certified provider credential.
                </p>

                {/* Chips */}
                <div className="flex items-center gap-2 flex-wrap mb-6">
                    <HeroChip icon={Star} label={trackMeta?.label || "GP Track"} />
                    <HeroChip icon={Clock} label={`${totalHrs} total`} />
                    <HeroChip icon={ListChecks} label={`${phases.length} phases · ${totalMods} modules`} />
                    {activePhase && (
                        <HeroChip
                            icon={Lightning}
                            label={`${activePhase.label} in progress`}
                            highlight
                        />
                    )}
                </div>

                {/* CTA row */}
                <div className="flex items-center gap-2.5 flex-wrap">
                    {currentModule && (
                        <button
                            type="button"
                            onClick={() => onOpenModule(currentModule.id)}
                            className="inline-flex items-center gap-2 px-5 py-3 bg-teal-500 hover:bg-teal-400 text-white rounded-lg font-bold text-[13.5px] tracking-tight transition-all shadow-lg shadow-teal-500/30 hover:-translate-y-0.5"
                            data-testid="hero-resume-cta"
                        >
                            <PlayCircle size={16} weight="fill" />
                            {progress.quiz_scores?.[currentModule.id] ? "Resume" : "Start"} Module {currentModule.index}
                            <ArrowRight size={13} weight="bold" />
                        </button>
                    )}
                    {!currentModule && activePhase && (
                        <div className="inline-flex items-center gap-2 px-5 py-3 bg-teal-500/20 border border-teal-500/40 text-teal-100 rounded-lg font-semibold text-[13px]">
                            <CheckCircle size={16} weight="fill" className="text-teal-300" />
                            All modules in {activePhase.label} complete
                        </div>
                    )}
                    <Link
                        to="/doctor/academy"
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white/8 border border-white/15 text-white/70 hover:bg-white/14 hover:text-white rounded-lg font-semibold text-[13px] transition-all no-underline"
                        data-testid="hero-catalogue-cta"
                    >
                        <Certificate size={13} weight="regular" />
                        View certificate requirements
                    </Link>
                </div>

                {/* Stats bar */}
                <div className="mt-7 pt-5 border-t border-white/8 flex gap-8 flex-wrap">
                    <HeroStat val={`${overallPct}%`} label="Overall progress" brand />
                    <HeroStat val={`${doneMods} / ${totalMods}`} label="Modules done" />
                    <HeroStat val={remainingHrs} label="Remaining" />
                    <HeroStat val={String(phases.length)} label="Phases" />
                </div>
            </div>
        </div>
    );
}


function HeroChip({ icon: Icon, label, highlight = false }) {
    if (highlight) {
        return (
            <div className="inline-flex items-center gap-1.5 bg-teal-500/25 border border-teal-500/50 text-teal-200 rounded-md px-2.5 py-1 text-[12px] font-semibold">
                {Icon && <Icon size={13} weight="fill" className="text-teal-300" />}
                {label}
            </div>
        );
    }
    return (
        <div className="inline-flex items-center gap-1.5 bg-white/7 border border-white/12 text-white/60 rounded-md px-2.5 py-1 text-[12px] font-medium">
            {Icon && <Icon size={13} weight="duotone" className="text-teal-300" />}
            {label}
        </div>
    );
}


function HeroStat({ val, label, brand = false }) {
    return (
        <div>
            <div
                className={`font-display text-[22px] font-bold tabular-nums leading-none tracking-tight ${
                    brand ? "text-teal-300" : "text-white"
                }`}
            >
                {val}
            </div>
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-white/30 mt-1.5">
                {label}
            </div>
        </div>
    );
}


/* ─── ProgressStrip — thin band under the hero ────────────────── */

function ProgressStrip({ progress, phases, activePhaseId }) {
    const phasesCompleted = new Set(progress?.phases_completed || []);
    const totalPhases = phases.length || 4;
    const activeRollup = activePhaseId ? progress?.phase_progress?.[activePhaseId] : null;
    const partial = activeRollup && !activeRollup.completed ? activeRollup.percent / 100 : 0;
    const overallPct = Math.round(
        Math.min(100, ((phasesCompleted.size + partial) / totalPhases) * 100),
    );
    const activePhaseIdx = phases.findIndex((p) => p.id === activePhaseId);
    const lockedCount = phases.filter(
        (p, i) => i > activePhaseIdx || (activePhaseIdx < 0 && !phasesCompleted.has(p.id)),
    ).length;

    return (
        <div
            className="bg-white border-b border-slate-200 px-6 md:px-12 py-3.5 flex items-center gap-5 flex-wrap"
            data-testid="progress-strip"
        >
            <span className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">Your progress</span>
            <div className="flex-1 max-w-[320px] h-1.5 bg-slate-100 rounded-full overflow-hidden min-w-[160px]">
                <div
                    className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-700"
                    style={{ width: `${overallPct}%` }}
                />
            </div>
            <span className="text-[12.5px] font-bold text-teal-700 tabular-nums whitespace-nowrap">
                {overallPct}% complete
            </span>
            <div className="w-px h-4 bg-slate-200" />
            <span className="text-[12px] font-medium text-slate-500">
                {activePhaseId ? (
                    <>Phase {phases.find((p) => p.id === activePhaseId)?.index || "?"} of {totalPhases} active</>
                ) : (
                    <>All caught up</>
                )}
                {lockedCount > 0 && <span className="text-slate-400"> · {lockedCount} locked</span>}
            </span>
        </div>
    );
}


/* ─── SectionHeading ──────────────────────────────────────────── */

function SectionHeading({ title, sub, className = "" }) {
    return (
        <div className={`mb-5 ${className}`}>
            <h2 className="font-display text-[18px] font-bold text-slate-900 tracking-tight mb-1">
                {title}
            </h2>
            {sub && <p className="text-[13px] text-slate-500 leading-relaxed max-w-2xl">{sub}</p>}
        </div>
    );
}


/* ─── PhaseStepper ────────────────────────────────────────────── */

function PhaseStepper({ phases, progress, activePhaseId }) {
    const phasesCompleted = new Set(progress?.phases_completed || []);
    return (
        <div
            className="bg-white border border-slate-200 rounded-xl px-6 py-5 flex items-start relative overflow-hidden mb-8"
            data-testid="phase-stepper"
        >
            {phases.map((phase, idx) => {
                const isDone = phasesCompleted.has(phase.id);
                const isActive = phase.id === activePhaseId;
                const PhaseIcon = PHASE_META_BY_ID[phase.id]?.icon || Books;
                const isLast = idx === phases.length - 1;

                let dotCls, dotIcon;
                if (isDone) {
                    dotCls = "bg-emerald-500 text-white";
                    dotIcon = <CheckCircle size={14} weight="fill" />;
                } else if (isActive) {
                    dotCls = "bg-teal-600 text-white shadow-[0_0_0_4px_rgba(0,124,157,0.15),0_0_0_5px_rgba(0,124,157,0.28)]";
                    dotIcon = <PhaseIcon size={14} weight="duotone" />;
                } else {
                    dotCls = "bg-slate-50 text-slate-400 border border-slate-200";
                    dotIcon = <Lock size={12} weight="regular" />;
                }

                return (
                    <div
                        key={phase.id}
                        className="flex-1 flex flex-col items-center gap-2 relative"
                        data-testid={`phase-stepper-node-${phase.id}`}
                    >
                        {!isLast && (
                            <div
                                className={`absolute top-[17px] left-1/2 right-[-50%] h-[2px] ${
                                    isDone ? "bg-emerald-500" : "bg-slate-100"
                                }`}
                                aria-hidden="true"
                            />
                        )}
                        <div className={`w-[34px] h-[34px] rounded-full flex items-center justify-center relative z-10 ${dotCls}`}>
                            {dotIcon}
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 text-center">
                            Phase {phase.index}
                        </div>
                        <div
                            className={`text-[12px] font-semibold text-center leading-tight max-w-[120px] ${
                                isActive ? "text-teal-700" : isDone ? "text-slate-700" : "text-slate-400"
                            }`}
                        >
                            {phase.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


/* ─── ActivePhaseCard — meta + progress ring ──────────────────── */

function ActivePhaseCard({ phase, progress, track }) {
    const rollup = progress.phase_progress?.[phase.id] || {
        completed_modules: 0,
        total_modules: phase.modules?.length || 0,
        percent: 0,
    };
    const PhaseIcon = PHASE_META_BY_ID[phase.id]?.icon || Books;
    const modules = phase.modules || [];
    const totalMins = modules.reduce((acc, m) => acc + (m.durations?.[track] || 0), 0);
    const hrsLabel = totalMins >= 60 ? `~${Math.round(totalMins / 60)} hours` : `${totalMins} min`;

    const ringSize = 56;
    const ringR = 22;
    const ringCirc = 2 * Math.PI * ringR;
    const ringOffset = ringCirc - (rollup.percent / 100) * ringCirc;

    return (
        <div
            className="bg-white border border-slate-200 rounded-xl px-6 py-5 mb-4 flex items-center justify-between gap-4 flex-wrap"
            data-testid="active-phase-card"
        >
            <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                    <PhaseIcon size={20} weight="duotone" className="text-teal-700" />
                </div>
                <div>
                    <div className="font-display text-[15px] font-bold text-slate-900 tracking-tight">
                        {phase.label} — Phase {phase.index} of {progress?.total_phases || 4}
                    </div>
                    <div className="text-[12px] text-slate-500 mt-0.5">
                        {modules.length} modules · {hrsLabel} · Complete all modules to unlock next phase
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400 mb-0.5">
                        Phase progress
                    </div>
                    <div className="text-[15px] font-bold text-teal-700 tabular-nums">
                        {rollup.completed_modules} / {rollup.total_modules} modules
                    </div>
                </div>
                <div className="relative" style={{ width: ringSize, height: ringSize }}>
                    <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
                        <circle
                            cx={ringSize / 2}
                            cy={ringSize / 2}
                            r={ringR}
                            fill="none"
                            stroke="#e5e7eb"
                            strokeWidth="4"
                        />
                        <circle
                            cx={ringSize / 2}
                            cy={ringSize / 2}
                            r={ringR}
                            fill="none"
                            stroke="#007C9D"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray={ringCirc}
                            strokeDashoffset={ringOffset}
                            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                            style={{ transition: "stroke-dashoffset 700ms ease-out" }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-teal-700 tabular-nums tracking-tight">
                        {rollup.percent}%
                    </div>
                </div>
            </div>
        </div>
    );
}


/* ─── ModuleAccordion + Item ──────────────────────────────────── */

function ModuleAccordion({ modules, phase, track, progress, onOpen }) {
    const isLockedPhase = phase.state !== "active";
    const [openId, setOpenId] = useState(null);
    // Auto-open the current module on mount
    useEffect(() => {
        const currentIdx = modules.findIndex((m) => !moduleIsComplete(m, progress));
        if (currentIdx >= 0 && !isLockedPhase) {
            setOpenId(modules[currentIdx].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase.id]);

    return (
        <div className="space-y-2" data-testid="module-accordion">
            {modules.map((m, idx) => {
                const isCompleted = moduleIsComplete(m, progress);
                const score = progress.quiz_scores?.[m.id];
                const priorAllComplete = modules.slice(0, idx).every((pm) => moduleIsComplete(pm, progress));
                const isCurrent = !isCompleted && priorAllComplete && !isLockedPhase;
                const isLocked = isLockedPhase || (!isCompleted && !isCurrent);
                return (
                    <ModuleAccordionItem
                        key={m.id}
                        module={m}
                        track={track}
                        isCompleted={isCompleted}
                        isCurrent={isCurrent}
                        isLocked={isLocked}
                        score={score}
                        isOpen={openId === m.id}
                        onToggle={() => setOpenId((prev) => (prev === m.id ? null : m.id))}
                        onOpen={() => onOpen(m.id)}
                    />
                );
            })}
        </div>
    );
}


function ModuleAccordionItem({
    module: m, track, isCompleted, isCurrent, isLocked, score, isOpen, onToggle, onOpen,
}) {
    const duration = m.durations?.[track];
    const format = m.format || "Video + Quiz";
    const ModIcon = MODULE_ICONS[(m.index - 1) % MODULE_ICONS.length] || BookOpen;

    // Container styling per state
    let containerCls = "bg-white border border-slate-200 rounded-xl overflow-hidden transition-shadow";
    if (isCurrent) containerCls += " border-teal-200 shadow-[0_0_0_2px_rgba(0,124,157,0.10)]";
    else if (!isLocked) containerCls += " hover:shadow-sm";
    else containerCls += " opacity-70";

    // Number tile styling
    let numCls, iconCls;
    if (isCompleted) {
        numCls = "bg-emerald-50 text-emerald-700";
        iconCls = "bg-emerald-50 text-emerald-700";
    } else if (isCurrent) {
        numCls = "bg-teal-50 text-teal-700";
        iconCls = "bg-teal-50 text-teal-700";
    } else {
        numCls = "bg-slate-50 text-slate-400";
        iconCls = "bg-slate-50 text-slate-400";
    }

    // Status pill
    let pill;
    if (isCompleted) {
        const pct = score?.best_score !== undefined ? Math.round(score.best_score * 100) : 100;
        pill = (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-[10px] font-bold uppercase tracking-[0.08em]">
                <CheckCircle size={10} weight="fill" /> {pct === 100 ? "Completed" : `${pct}% passed`}
            </span>
        );
    } else if (isCurrent) {
        pill = (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-[10px] font-bold uppercase tracking-[0.08em]">
                <Lightning size={10} weight="fill" /> In progress
            </span>
        );
    } else {
        pill = (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 text-slate-400 border border-slate-200 rounded-full text-[10px] font-bold uppercase tracking-[0.08em]">
                <Lock size={9} weight="regular" /> Locked
            </span>
        );
    }

    const headerCls = `flex items-center gap-3.5 px-4 md:px-5 py-3.5 ${
        isLocked ? "cursor-default" : "cursor-pointer hover:bg-slate-50/50"
    } transition-colors`;

    // Retry percentage for non-completed but attempted quiz
    const attempted = !isCompleted && score && score.best_score !== undefined;
    const attemptPct = attempted ? Math.round(score.best_score * 100) : null;

    return (
        <div className={containerCls} data-testid={`module-item-${m.id}`}>
            <div
                className={headerCls}
                onClick={isLocked ? undefined : onToggle}
                role={isLocked ? undefined : "button"}
                tabIndex={isLocked ? undefined : 0}
                onKeyDown={(e) => {
                    if (!isLocked && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onToggle();
                    }
                }}
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-[11.5px] font-bold tabular-nums ${numCls}`}>
                    {String(m.index).padStart(2, "0")}
                </div>
                <div className={`hidden sm:flex w-10 h-10 rounded-lg items-center justify-center shrink-0 ${iconCls}`}>
                    {isLocked ? <Lock size={16} weight="regular" /> : <ModIcon size={17} weight="fill" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`font-bold text-[14px] leading-tight tracking-tight ${isLocked ? "text-slate-500" : "text-slate-900"}`}>
                        {m.title}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap mt-1">
                        <ModuleMetaTag icon={Play} label={format} />
                        {duration && <ModuleMetaTag icon={Clock} label={`${duration} min`} />}
                        {attempted && (
                            <ModuleMetaTag icon={Exam} label={`Attempted · ${attemptPct}%`} />
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2.5 shrink-0">
                    {pill}
                    {isCurrent && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpen(); }}
                            className="hidden md:inline-flex items-center gap-1 px-4 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-[12.5px] font-bold transition-colors"
                            data-testid={`module-resume-${m.id}`}
                        >
                            <Play size={11} weight="fill" />
                            {attempted ? "Resume" : "Start"}
                        </button>
                    )}
                    {!isLocked && (
                        isOpen
                            ? <CaretUp size={12} weight="bold" className={isCurrent ? "text-teal-700" : "text-slate-400"} />
                            : <CaretDown size={12} weight="bold" className="text-slate-400" />
                    )}
                </div>
            </div>
            {isOpen && !isLocked && (
                <ModuleExpandPanel
                    module={m}
                    track={track}
                    isCompleted={isCompleted}
                    isCurrent={isCurrent}
                    attemptPct={attemptPct}
                    onOpen={onOpen}
                />
            )}
        </div>
    );
}


function ModuleMetaTag({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-500 font-medium">
            {Icon && <Icon size={11} weight="regular" />}
            {label}
        </span>
    );
}


function ModuleExpandPanel({ module: m, track, isCompleted, isCurrent, attemptPct, onOpen }) {
    // Build a preview of topics for this module + track
    const topics = m.tracks?.[track]?.topics || [];
    const displayable = topics
        .filter((t) => t.depth !== "none")
        .slice(0, 6)
        .map((t) => t.label);

    return (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 md:px-5 py-4 md:pl-[76px]">
            {m.summary && (
                <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
                    {m.summary}
                </p>
            )}
            {isCurrent && attemptPct !== null && (
                <div className="flex items-center gap-2.5 mb-3">
                    <div className="flex-1 max-w-[220px] h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-teal-500 to-teal-400"
                            style={{ width: `${attemptPct}%` }}
                        />
                    </div>
                    <span className="text-[11.5px] font-bold text-teal-700 tabular-nums">
                        {attemptPct}% best score
                    </span>
                </div>
            )}
            {displayable.length > 0 && (
                <>
                    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 mb-2">
                        Topics covered
                    </div>
                    <div className="flex flex-col gap-1">
                        {displayable.map((label, i) => (
                            <div key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700 leading-snug">
                                {isCompleted ? (
                                    <CheckCircle size={12} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                                ) : (
                                    <Circle size={11} weight="regular" className="text-slate-300 shrink-0 mt-0.5" />
                                )}
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {(isCurrent || isCompleted) && (
                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onOpen}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-[12.5px] font-bold transition-colors"
                        data-testid={`module-open-${m.id}`}
                    >
                        {isCompleted ? "Review module" : (attemptPct !== null ? "Resume module" : "Start module")}
                        <ArrowRight size={12} weight="bold" />
                    </button>
                </div>
            )}
        </div>
    );
}


/* ─── CertificateBanner ────────────────────────────────────────── */

function CertificateBanner({ progress, content }) {
    const phasesCompleted = progress?.phases_completed || [];
    if (phasesCompleted.length === 0) return null;
    const latestId = phasesCompleted[phasesCompleted.length - 1];
    const phase = content?.phases?.find((p) => p.id === latestId);
    if (!phase) return null;
    return (
        <div
            className="relative overflow-hidden bg-slate-900 rounded-2xl p-6 md:p-7 mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 border border-slate-800 shadow-lg"
            data-testid="certificate-download-banner"
            data-phase-id={phase.id}
        >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_right,rgba(0,124,157,0.30),transparent_50%),radial-gradient(circle_at_bottom_left,hsla(340,75%,45%,0.20),transparent_50%)]" />
            <div className="relative flex items-start gap-4 flex-1">
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Certificate size={20} weight="duotone" className="text-teal-300" />
                </div>
                <div>
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-teal-300 mb-1">
                        <Sparkle size={11} weight="fill" />
                        Milestone unlocked
                    </div>
                    <h4 className="font-display text-[18px] md:text-[20px] font-bold text-white tracking-tight leading-tight">
                        Phase {phase.index}: {phase.label} — complete
                    </h4>
                    <p className="text-[13px] text-slate-300 leading-relaxed mt-1.5 max-w-xl">
                        Congratulations. Download your ICPP Phase {phase.index} certificate and share it with patients and colleagues.
                    </p>
                </div>
            </div>
            <button
                type="button"
                onClick={() => downloadCertificate(phase.id)}
                className="relative bg-white text-slate-900 px-5 py-2.5 rounded-full font-bold text-[13px] hover:scale-105 transition-transform flex items-center gap-2 shrink-0 shadow-lg"
                data-testid={`academy-download-cert-${phase.id}`}
            >
                <DownloadSimple size={13} weight="bold" />
                Download PDF
            </button>
        </div>
    );
}


/* ─── LockedPhaseBlocks — bottom of page ──────────────────────── */

function LockedPhaseBlocks({ phases, activePhaseId, phasesCompleted, progress }) {
    const upcoming = phases.filter(
        (p) => p.id !== activePhaseId && !phasesCompleted.includes(p.id),
    );
    if (upcoming.length === 0) return null;
    return (
        <div className="mt-10" data-testid="academy-locked-phases">
            <SectionHeading
                title="Upcoming Phases"
                sub="Locked until you complete the previous phase."
            />
            <div className="space-y-2.5">
                {upcoming.map((phase) => {
                    const PhaseIcon = PHASE_META_BY_ID[phase.id]?.icon || Books;
                    const modCount = phase.modules?.length || 0;
                    const priorLabel = phase.index > 1 ? `Unlocks after Phase ${phase.index - 1}` : "Locked";
                    const canDownloadCert = progress?.phases_completed?.includes(phase.id);
                    return (
                        <div
                            key={phase.id}
                            className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center gap-4 opacity-70"
                            data-testid={`locked-phase-${phase.id}`}
                            data-phase-id={phase.id}
                        >
                            <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                                <PhaseIcon size={18} weight="duotone" className="text-slate-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-[13.5px] text-slate-600 tracking-tight">
                                    Phase {phase.index} — {phase.label}
                                </div>
                                <div className="text-[12px] text-slate-400 mt-0.5">
                                    {modCount > 0 ? `${modCount} modules · ${priorLabel}` : `Coming soon · ${priorLabel}`}
                                </div>
                            </div>
                            {canDownloadCert ? (
                                <button
                                    type="button"
                                    onClick={() => downloadCertificate(phase.id)}
                                    className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-700 hover:text-emerald-800 transition-colors"
                                >
                                    <Certificate size={12} weight="fill" /> Certificate
                                </button>
                            ) : (
                                <Lock size={15} weight="fill" className="text-slate-300 shrink-0" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function NoActivePhaseNotice({ phasesCompleted }) {
    return (
        <div
            className="bg-white rounded-xl border border-slate-200 p-8 md:p-10 text-center"
            data-testid="academy-no-active-phase"
        >
            <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-100 flex items-center justify-center mx-auto mb-4">
                <Trophy size={26} weight="duotone" className="text-teal-700" />
            </div>
            <h3 className="font-display text-[18px] font-bold text-slate-900 tracking-tight">
                {phasesCompleted > 0 ? "You're all caught up" : "No active phase"}
            </h3>
            <p className="text-[13px] text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                {phasesCompleted > 0
                    ? `You've completed ${phasesCompleted} phase${phasesCompleted === 1 ? "" : "s"}. The next phase will unlock once released.`
                    : "New content will appear here as it's released."}
            </p>
        </div>
    );
}


/* ─── Track selection screen — unchanged behaviour ─────────────── */

function TrackSelectionScreen({ onPick }) {
    return (
        <div className="min-h-screen bg-slate-50" data-testid="academy-track-picker">
            <div className="max-w-4xl mx-auto px-6 md:px-8 py-8">
                <Link
                    to="/doctor/academy"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline mb-3"
                    data-testid="icpp-back-to-catalogue"
                >
                    <ArrowLeft size={11} weight="bold" /> Invisique Academy
                </Link>

                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                        <Trophy size={20} weight="duotone" className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary/80">
                            Invisique™ Certified Provider Program
                        </div>
                        <h1
                            className="font-display text-xl sm:text-2xl font-bold tracking-tight text-foreground mt-0.5"
                            data-testid="academy-title"
                        >
                            Choose your track
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Your learning modules will be tailored to your practice type.{" "}
                            <strong className="text-foreground">This is a one-time choice</strong> — the track cannot be changed after selection.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {["gp", "ortho"].map((t) => (
                        <TrackCard key={t} track={t} onPick={() => onPick(t)} />
                    ))}
                </div>

                <div
                    className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 leading-relaxed flex items-start gap-2"
                    data-testid="academy-track-warning"
                >
                    <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <strong>Choose carefully.</strong> Your track determines your curriculum and certificate. Once selected, it cannot be changed — pricing and curriculum will differ per specialty in future updates.
                    </div>
                </div>
            </div>
        </div>
    );
}


function TrackCard({ track, onPick }) {
    const meta = TRACK_META[track];
    const Icon = meta.icon;
    return (
        <button
            type="button"
            onClick={onPick}
            data-testid={`academy-track-select-${track}`}
            className={`relative bg-white rounded-2xl bento-shadow p-5 text-left flex flex-col gap-3 transition-all hover:shadow-[0_4px_16px_rgba(0,45,57,0.08)] hover:-translate-y-0.5 group ${meta.borderHoverCls}`}
        >
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.iconTileCls}`}>
                    <Icon size={20} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-[11px] font-bold uppercase tracking-[0.14em] ${meta.subtitleCls}`}>
                        {meta.subtitle}
                    </div>
                    <div className="font-display text-[15px] font-bold text-foreground leading-tight">
                        {meta.label}
                    </div>
                </div>
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">{meta.pitch}</p>
            <ul className="flex flex-col gap-1.5 text-xs text-foreground/80">
                {meta.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <CheckCircle size={12} weight="fill" className={`${meta.text} shrink-0 mt-0.5`} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <div className={`mt-1 w-full h-9 rounded-md font-semibold text-sm flex items-center justify-center gap-1.5 ${meta.buttonCls}`}>
                Select {meta.label}
                <ArrowRight size={13} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </div>
        </button>
    );
}


function ConfirmTrackDialog({ track, onConfirm, onCancel }) {
    const [cached, setCached] = useState(null);
    useEffect(() => { if (track) setCached(track); }, [track]);
    const activeTrack = track || cached;
    const meta = activeTrack ? TRACK_META[activeTrack] : null;
    return (
        <Dialog open={!!track} onOpenChange={(open) => { if (!open) onCancel(); }}>
            <DialogContent
                aria-labelledby="icpp-confirm-title"
                aria-describedby="icpp-confirm-desc"
                data-testid="academy-track-confirm-dialog"
            >
                <DialogHeader>
                    <DialogTitle id="icpp-confirm-title">
                        {meta ? `Enroll in the ${meta.label}?` : "Confirm track"}
                    </DialogTitle>
                    <DialogDescription id="icpp-confirm-desc">
                        This is a one-time decision. You will not be able to switch tracks later — pricing and curriculum will differ per specialty in future updates. Contact Invisique support if you make a mistake.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} data-testid="academy-track-confirm-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        className={meta?.button || "bg-primary hover:bg-primary/90 text-primary-foreground"}
                        data-testid="academy-track-confirm-ok"
                    >
                        Yes, lock in {meta?.label || "this track"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── Helpers (module scope) ──────────────────────────────────── */

async function downloadCertificate(phaseId) {
    try {
        const resp = await api.get(`/academy/certificate/${phaseId}`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        window.open(url, "_blank");
    } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't fetch certificate.");
    }
}
